/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { ResponsiveGrid } from './ResponsiveGrid';

type ComponentMappings = Record<string, React.ComponentType<any>>;

interface AemMockRendererProps {
  content: any;
  path?: string;
  isEditMode?: boolean;
  componentMappings?: ComponentMappings;
}

const DEFAULT_COMPONENT_MAPPINGS: ComponentMappings = {
  'wcm/foundation/components/responsivegrid': ResponsiveGrid,
  'mi-aem-common-spa/components/container': ResponsiveGrid,
  'mi-aem-common-spa/components/xfpage': ResponsiveGrid,
  'core/wcm/components/page/v2/page': ResponsiveGrid,
};

export function AemMockRenderer({
  content,
  path = '',
  isEditMode = false,
  componentMappings = {},
}: AemMockRendererProps): JSX.Element | null {
  if (!content) return null;

  const AEM_COMPONENT_MAP = {
    ...DEFAULT_COMPONENT_MAPPINGS,
    ...componentMappings,
  };
  console.log('[AEMMockRenderer] Component Mappings recieved', componentMappings);

  // Handle page root
  if (
    content[':type'] === 'core/wcm/components/page/v2/page' ||
    content[':type'] === 'mi-aem-common-spa/components/xfpage'
  ) {
    const { 'jcr:content': jcrContent = {}, ':items': items = {}, ':itemsOrder': order = Object.keys(items) } = content;

    return (
      <div
        className="root"
        data-cq-path={path}
        data-cq-data-path={path}
        data-cq-editor={isEditMode ? 'page' : undefined}
      >
        <AemMockRenderer
          content={{
            ...jcrContent,
            ':items': items,
            ':itemsOrder': order,
          }}
          path={path}
          isEditMode={isEditMode}
        />
      </div>
    );
  }

  // Handle container components
  if (content[':items'] && Array.isArray(content[':itemsOrder'])) {
    const { ':items': items = {}, ':itemsOrder': order = Object.keys(items) } = content;

    return (
      <div className="aem-container" data-cq-path={path} data-cq-data-path={path}>
        {order.map((key: string) => {
          const item = items[key];
          if (!item) return null;

          return <AemMockRenderer key={key} content={item} path={`${path}/${key}`} isEditMode={isEditMode} />;
        })}
      </div>
    );
  }

  // Handle individual components
  const Component = AEM_COMPONENT_MAP[content[':type'] as keyof typeof AEM_COMPONENT_MAP];
  if (!Component) {
    console.warn(`🟡 [AEMMockRenderer] No mapping found for component type: ${content[':type']}`);
    return null;
  }

  // Filter out internal AEM properties before passing to component
  const { ':items': _items, ':itemsOrder': _itemsOrder, ':type': _type, ...componentProps } = content;

  return (
    <div
      className={`aem-component ${content[':type']?.replace(/\//g, '-')}`}
      data-cq-path={path}
      data-cq-data-path={path}
    >
      <Component {...componentProps} isInEditor={isEditMode} />
    </div>
  );
}
